// Our text might contain special chars that will trip up the RegEx
// This will escape them all
const rxSpecialChars = /[-[\]{}()*+?.,\\^$|#]/g

/**
 * @description Escape regex special characters in a string.
 * @param {string} str - The string to escape.
 * @returns {string} The escaped string.
 */
const escapeRxSpecialChars = (str) => str.replace(rxSpecialChars, '\\$&')

// Maps characters that are unsafe to render as literal HTML to their escaped entity
const htmlEscapes = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}
const htmlEscapeChars = /[&<>"']/g

/**
 * @description Escape HTML special characters in a string so that it is rendered as literal
 * text (rather than being parsed as markup/elements) when bound via `v-html`. Document text
 * is extracted from OCR/PDF sources and may contain arbitrary substrings that look like HTML
 * (e.g. a stray `<s` or `<a`), so this must be applied before any trusted HTML (e.g. `<br>`,
 * `<mark>`) is added by the document reader.
 * @param {string} str - The string to escape.
 * @returns {string} The escaped string.
 */
const escapeHtml = (str) => str.replace(htmlEscapeChars, (char) => htmlEscapes[char])
// Whitelist: character after the needle must be one of these
const wordEnding = '[\\n\\s\\.\\?\\)!:<]'
// Blacklist: character before the needle must not be alphanumeric
const wordStart = '(?<![A-Za-z0-9])'

/**
 * @description Replace the nth occurrence of a string (`f`) with another string (`r`) in a string (`s`).
 * @example
 *   replaceNth('I have two dogs', 'dogs', 'cats', 1)
 *   // 'I have two cats'
 *   replaceNth('I have two dogs', 'dogs', 'cats', 2)
 *   // 'I have two dogs' (no change as there is no second occurrence)
 * @param {string} s - The string to search.
 * @param {string} f - TThe string to find (case insensitive). Regex special characters are escaped.
 * @param {string} r - The string to replace with.
 * @param {number} n - The nth occurrence to replace.
 * @returns {string} The modified string.
 */
const replaceNth = (s, f, r, n) => {
  // Escape special characters in the string we are searching for
  const needle = escapeRxSpecialChars(f)

  // We intentionally trim the string and then add a [space] to the end, to help the regex with ${wordEnding} at the end of text
  const haystack = `${s.trim()} `

  // Finds the nth bounded occurrence of needle, so it can't match a substring of a larger word (e.g. 'discharge' inside 'EDISCHARGE')
  const pattern = `^(?:.*?${wordStart}${needle}${wordEnding}){${n}}`
  // Create a regex using flags: g: global, i: case-insensitive, s: single (. matches newlines)
  const rx = RegExp(pattern, 'gis')

  // replace the needle, retaining the wordEnding and trim off the space added to the haystack earlier on
  return haystack
    .replace(rx, (x) => x.replace(RegExp(`${wordStart}(${needle})(${wordEnding})$`, 'i'), `${r}$2`))
    .trim()
}

/**
 * @description Wrap the nth occurrence of a string with another string.
 * @example
 *   wrapNth('I have two dogs', 'dogs', '#MATCH#', 1)
 *   // 'I have two #MATCH#dogs#MATCH#'
 *   wrapNth('I have two dogs', 'dogs', '#MATCH#', 2)
 *   // 'I have two dogs' (no change as there is no second occurrence)
 * @param {any} s- The string to search.
 * @param {any} f - The string to find (case insensitive). Regex special characters are escaped.
 * @param {any} r - The string to wrap with.
 * @param {any} n - The nth occurrence to wrap.
 * @returns {string} The modified string.
 */
const wrapNth = (s, f, r, n) => replaceNth(s, f, `${r}$1${r}`, n)

/**
 * @description Find the start index of every word-bounded occurrence of `searchStr` in `str`.
 * A match must not be a substring of a larger word (e.g. 'discharge' inside 'discharged').
 * @param {string} searchStr - The string to search for.
 * @param {string} str - The string to search within.
 * @param {boolean} [caseSensitive] - Whether the search should be case sensitive.
 * @returns {number[]} The starting index of each match.
 */
const getIndicesOf = (searchStr, str, caseSensitive) => {
  // Trim first: a needle that already ends in whitespace (e.g. a sentence with a trailing "\n\n")
  // would otherwise need a wordEnding character *after* that whitespace too, which may not exist
  const trimmedSearchStr = searchStr?.trim() ?? ''

  if (trimmedSearchStr.length === 0) {
    return []
  }

  const rx = RegExp(
    `${wordStart}${escapeRxSpecialChars(trimmedSearchStr)}(?=${wordEnding}|$)`,
    caseSensitive ? 'g' : 'gi',
  )
  const indices = []
  let match

  while ((match = rx.exec(str)) !== null) {
    indices.push(match.index)
  }

  return indices
}

export { escapeRxSpecialChars, escapeHtml, replaceNth, wrapNth, getIndicesOf }
